import React, { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthGate } from "./components/AuthGate.js";
import { MailRoute } from "./modules/mail/MailRoute.js";
import { useIsMobileViewport } from "./hooks/useIsMobileViewport.js";
import { PageSkeleton } from "./components/Skeleton.js";
import { ToastProvider } from "./components/Toasts.js";
import { AppUpdateNotice } from "./components/AppUpdate.js";
import { SettingsShell } from "./components/SettingsShell.js";
import { lazyPage } from "./lib/lazyPage.js";

/* Every route is split out, Mail included.
 *
 * MailRoute itself is small and eager and lazily loads the desktop mail page
 * (modules/mail/MailRoute.tsx) behind a placeholder shaped like the mail
 * list. A phone loads none of this: it gets the phone app (src/phone).
 *
 * Calendar, Tasks, the Assistant and the settings pages were once downloaded
 * and parsed before the Inbox could render. They have no business blocking
 * mail.
 *
 * Each lazy import gets a skeleton via the Suspense boundary below, so a slow
 * chunk shows the shape of a page rather than a blank screen. */
const MailSettings = lazyPage(() => import("./pages/MailSettings.js").then((m) => ({ default: m.MailSettings })));
const SettingsAi = lazyPage(() => import("./pages/SettingsAi.js").then((m) => ({ default: m.SettingsAi })));
const MailSignaturesSettings = lazyPage(() =>
  import("./pages/MailSignaturesSettings.js").then((m) => ({ default: m.MailSignaturesSettings })),
);
const MailCacheSettings = lazyPage(() =>
  import("./pages/MailCacheSettings.js").then((m) => ({ default: m.MailCacheSettings })),
);
const MailJobsPage = lazyPage(() => import("./modules/mail/admin/index.js").then((m) => ({ default: m.MailJobsPage })));
const CalendarRoute = lazyPage(() => import("./modules/calendar/index.js").then((m) => ({ default: m.CalendarRoute })));
const CalendarSettingsPage = lazyPage(() =>
  import("./modules/calendar/index.js").then((m) => ({ default: m.CalendarSettingsPage })),
);
const TasksPage = lazyPage(() => import("./modules/tasks/index.js").then((m) => ({ default: m.TasksPage })));
const HomePage = lazyPage(() => import("./modules/home/HomePage.js").then((m) => ({ default: m.HomePage })));
/** The phone app (src/phone): its own shell, screens and navigation. */
const PhoneApp = lazyPage(() => import("./phone/PhoneApp.js").then((m) => ({ default: m.PhoneApp })));
const AskPage = lazyPage(() => import("./modules/assistant/AskPage.js").then((m) => ({ default: m.AskPage })));
const TravelPage = lazyPage(() => import("./modules/travel/TravelPage.js").then((m) => ({ default: m.TravelPage })));

/** WOV SPA root.
 *
 *  Every desktop page lays itself out in the shared panes
 *  (components/Panes.tsx): one left column, the nav pane, carries the apps,
 *  the page's nav, and Settings, Appearance, Reload and the account. There
 *  is no separate app rail (2026-10-02).
 *
 *  A phone gets a different app, not a squeezed desktop (2026-10-03): at
 *  phone width the phone app (src/phone) mounts instead of these routes,
 *  with its own tabs (Today, Inbox, Tasks, Assistant), and only its own
 *  chunk is downloaded. */
export function App(): JSX.Element {
  const phone = useIsMobileViewport();
  if (phone) {
    return (
      <AuthGate>
        <ToastProvider>
          <AppUpdateNotice />
          <Suspense fallback={<PageSkeleton />}>
            <PhoneApp />
          </Suspense>
        </ToastProvider>
      </AuthGate>
    );
  }
  return (
    <AuthGate>
      <ToastProvider>
      <AppUpdateNotice />
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
        <main
          id="main-content"
          className="min-h-screen"
        >
          <Suspense fallback={<PageSkeleton />}>
            <Routes>
              {/* Home: the dashboard the app opens on (2026-10-02). */}
              <Route path="/" element={<HomePage />} />
              {/* The mail viewer, and its folder/message routes. */}
              <Route path="/mail" element={<MailRoute />} />
              {/* Search results, from the title bar anywhere (TitleBar.tsx). */}
              <Route path="/search" element={<MailRoute />} />
              {/* A question from the title bar: answered, not searched (2026-10-06). */}
              <Route path="/ask" element={<AskPage />} />
              <Route path="/mail/folder/:folderId" element={<MailRoute />} />
              <Route path="/mail/folder/:folderId/message/:messageId" element={<MailRoute />} />
              {/* One message in its own window (a double-click on a row). */}
              <Route path="/mail/message/:messageId" element={<MailRoute />} />

              {/* Calendar surface */}
              <Route path="/calendar" element={<CalendarRoute />} />
              {/* The Recurring audit, inside the calendar's panes. */}
              <Route path="/calendar/recurring" element={<CalendarRoute />} />
              {/* Travel moved to an app of its own (2026-10-06). */}
              <Route path="/calendar/travel" element={<Navigate to="/travel" replace />} />

              {/* Travel: trips from the calendar and the agent's itineraries. */}
              <Route path="/travel" element={<TravelPage />} />

              {/* Outlook To-Do surface: native tasks + flagged mail */}
              <Route path="/tasks" element={<TasksPage />} />

              {/* The Assistant page folded into the title-bar search (2026-10-06):

                  a question there gets an answer card above the results. */}

              <Route path="/assistant" element={<Navigate to="/" replace />} />

              {/* Settings, in the settings panes */}
              <Route element={<SettingsShell />}>
                <Route path="/settings" element={<Navigate to="/settings/mail" replace />} />
                <Route path="/settings/mail" element={<MailSettings />} />
                <Route path="/settings/mail/signatures" element={<MailSignaturesSettings />} />
                <Route path="/settings/mail/cache" element={<MailCacheSettings />} />
                <Route path="/settings/calendar" element={<CalendarSettingsPage />} />
                <Route path="/settings/ai" element={<SettingsAi />} />
                <Route path="/settings/ai/features" element={<SettingsAi />} />
                <Route path="/mail/jobs" element={<MailJobsPage />} />
              </Route>
            </Routes>
          </Suspense>
        </main>
      </div>
      </ToastProvider>
    </AuthGate>
  );
}
